import { hashWords, toUnitFloat } from "daedalus-core";
import type { PuddleClass } from "./wetness.ts";

/**
 * The shape of a puddle (ADR-144 build note, P2.27-T20): its descriptor, the field of values from 0 to 1 that is cut at a
 * level to give its outline at a fill, and the seeded hash the placement and the shape draw from. A puddle is merged
 * ellipses stretched along the path or wall it lies by with a chunky hashed raggedness; growing and drying only move the cut,
 * so every shape on the way is a subset of the full one. Pure.
 */

/**
 * How wide one cell of a puddle's field is, in world pixels.
 */
export const PUDDLE_CELL_PX = 2;

/**
 * The field value the full-size shape is cut at, and the lowest value the soaked rim reaches.
 */
export const FULL_CUT = 0.25;
export const RIM_CUT = 0.09;

const GROW_START = 0.12;
const GROW_CURVE = 0.7;

export const DOMAIN_SHAPE = 0x70756434;
const DOMAIN_NOISE = 0x70756435;
const SHARPEN = 0.75;
const EDGE_POWER = 2.5;

/**
 * How far from a blob's centre, along either of its axes in units of its radius, a cell can still take water from it. The
 * blob's edge measure is never less than the square of the larger axis distance, and a blob adds nothing once that measure
 * reaches `1 / SHARPEN`, so a cell at or past the square root of that on either axis is skipped without the powers. The
 * value sits just above the root, so the skip only ever drops a term that is exactly 0.
 */
const BLOB_REACH = Math.sqrt(1 / SHARPEN) + 0.001;
const words = new Uint32Array(6);

/**
 * The field value a puddle is cut at for a fill: nothing is above `peak` for a nothing fill, and `FULL_CUT` gives the whole
 * shape. The cut falls along a curve fitted so the water's area, not the cut, grows evenly with the fill across the size
 * classes: a field is flat near its top and wide near its edge, so an even fall of the cut would flood most of a puddle
 * in its first part and add little in its last.
 *
 * @param fill How full the puddle is, 0 to 1.
 * @param peak The highest value of the puddle's field (`peakOf`).
 * @returns The cut.
 */
export function cutOf(fill: number, peak: number): number {
  const reached = fill <= 0 ? 0 : fill >= 1 ? 1 : GROW_START + (1 - GROW_START) * fill ** GROW_CURVE;
  return FULL_CUT + (peak - FULL_CUT) * (1 - reached);
}

const peaks = new WeakMap<Puddle, number>();

/**
 * The highest value a puddle's field reaches, found once by reading every cell and kept for the puddle.
 *
 * @param puddle The puddle.
 * @returns The peak, above `FULL_CUT`.
 */
export function peakOf(puddle: Puddle): number {
  const known = peaks.get(puddle);
  if (known !== undefined) return known;
  const x0 = puddle.x - puddle.radius;
  const y0 = puddle.y - puddle.radius;
  const gx = Math.round(x0 / PUDDLE_CELL_PX);
  const gy = Math.round(y0 / PUDDLE_CELL_PX);
  let peak = FULL_CUT;
  for (let cy = 0; cy < puddle.radius; cy += 1) {
    for (let cx = 0; cx < puddle.radius; cx += 1) {
      peak = Math.max(peak, Math.fround(fieldAt(puddle, x0 + cx * PUDDLE_CELL_PX + 1, y0 + cy * PUDDLE_CELL_PX + 1, gx + cx, gy + cy)));
    }
  }
  peaks.set(puddle, peak);
  return peak;
}

/**
 * One puddle. `x` and `y` are its centre in world pixels, `radius` the half side of the square its field fills, in pixels.
 * `key` is its stable name, from the map and the tile it was placed on (never its size class, which can fall back to a smaller one), so a server and every page name the same puddle and a message about it means the same one on each. `blobs` holds four numbers per merged ellipse in the puddle's own frame (along the axis, across it, radius along,
 * radius across), `cos` and `sin` the axis.
 */
export interface Puddle {
  readonly id: number;
  readonly key: string;
  readonly cls: PuddleClass;
  readonly unit: number;
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly scale: number;
  readonly cos: number;
  readonly sin: number;
  readonly blobs: Float32Array;
  readonly seed: number;
}

/**
 * A hash of up to five integers as a number from 0 to 1.
 *
 * @param seed The world's seed.
 * @param a First integer.
 * @param b Second integer.
 * @param c Third integer.
 * @param d Fourth integer.
 * @returns A value in 0 up to but not including 1.
 */
export function unit(seed: number, a: number, b: number, c: number, d: number): number {
  words[0] = a >>> 0;
  words[1] = b >>> 0;
  words[2] = c >>> 0;
  words[3] = d >>> 0;
  return toUnitFloat(hashWords(seed >>> 0, words, 4));
}

/**
 * Builds one puddle's shape at a scale: its merged ellipses, laid along the axis with hashed jitter and radii.
 *
 * @param seed The seed.
 * @param id The puddle's index.
 * @param key The puddle's stable name.
 * @param cls The size class.
 * @param elongation How long the evidence says the puddle is (1 to 2).
 * @param angle The axis angle.
 * @param scale The scale of every radius.
 * @param x The centre's x.
 * @param y The centre's y.
 * @param unitOnset The puddle's seeded number for its onset.
 * @returns The puddle.
 */
export function shapeOf(seed: number, id: number, key: string, cls: PuddleClass, elongation: number, angle: number, scale: number, x: number, y: number, unitOnset: number): Puddle {
  const roll = (a: number, b: number): number => unit(seed, DOMAIN_SHAPE, id, a, b);
  const count = cls === "small" ? 1 + Math.floor(roll(0, 0) * 2) : cls === "medium" ? 3 + Math.floor(roll(0, 0) * 2) : 5 + Math.floor(roll(0, 0) * 4);
  const blobs = new Float32Array(count * 4);
  const lo = cls === "large" ? 16 : cls === "medium" ? 13 : 11;
  const hi = cls === "large" ? 26 : cls === "medium" ? 20 : 17;
  const bend = (roll(0, 5) - 0.5) * (cls === "small" ? 6 : 26) * scale;
  let gap = 0;
  for (let i = 0; i < count; i += 1) {
    const belly = count === 1 ? 1 : 0.65 + 0.45 * Math.sin((Math.PI * (i + 0.5)) / count);
    const radius = (lo + (hi - lo) * roll(i + 1, 2)) * belly * scale;
    const stretch = (1.3 + 0.5 * roll(i + 1, 3)) * elongation;
    blobs[i * 4 + 2] = radius * stretch;
    blobs[i * 4 + 3] = radius * (0.95 + 0.3 * roll(i + 1, 6));
    gap += radius * stretch;
  }
  gap = (gap / count) * 0.75;
  let reach = 0;
  for (let i = 0; i < count; i += 1) {
    const t = count === 1 ? 0 : (i - (count - 1) / 2) / ((count - 1) / 2);
    const along = (i - (count - 1) / 2) * gap;
    const across = (roll(i + 1, 4) - 0.5) * 1.1 * (blobs[i * 4 + 3] as number) + bend * t * t;
    blobs[i * 4] = along;
    blobs[i * 4 + 1] = across;
    reach = Math.max(reach, Math.hypot(along, across) + Math.max(blobs[i * 4 + 2] as number, blobs[i * 4 + 3] as number));
  }
  const radius = Math.ceil((reach * 1.12 + 8) / (2 * PUDDLE_CELL_PX)) * 2 * PUDDLE_CELL_PX;
  return { id, key, cls, unit: unitOnset, x, y, radius, scale, cos: Math.cos(angle), sin: Math.sin(angle), blobs, seed };
}

/**
 * The value of a puddle's field at a world point: the merged ellipses, each falling from 1 at its centre to `FULL_CUT` at its
 * edge, summed (above 1 where blobs overlap), with a chunky hashed raggedness so no edge is a clean curve.
 *
 * @param puddle The puddle.
 * @param px World x.
 * @param py World y.
 * @param cellX The field cell's column, for the raggedness.
 * @param cellY The field cell's row.
 * @returns A value from 0 up; the full shape lies above `FULL_CUT`.
 */
export function fieldAt(puddle: Puddle, px: number, py: number, cellX: number, cellY: number): number {
  const dx = px - puddle.x;
  const dy = py - puddle.y;
  const u = dx * puddle.cos + dy * puddle.sin;
  const w = -dx * puddle.sin + dy * puddle.cos;
  const blobs = puddle.blobs;
  let sum = 0;
  for (let i = 0; i < blobs.length; i += 4) {
    const du = (u - (blobs[i] as number)) / (blobs[i + 2] as number);
    if (du >= BLOB_REACH || du <= -BLOB_REACH) continue;
    const dw = (w - (blobs[i + 1] as number)) / (blobs[i + 3] as number);
    if (dw >= BLOB_REACH || dw <= -BLOB_REACH) continue;
    const edge = (Math.abs(du) ** EDGE_POWER + Math.abs(dw) ** EDGE_POWER) ** (2 / EDGE_POWER);
    const d2 = edge;
    if (d2 < 1.6) sum += Math.max(0, 1 - d2 * SHARPEN);
  }
  if (sum <= 0) return 0;
  const fine = unit(puddle.seed, DOMAIN_NOISE, puddle.id, cellX + 0x8000, cellY + 0x8000);
  const coarse = unit(puddle.seed, DOMAIN_NOISE + 1, puddle.id, (cellX >> 2) + 0x8000, (cellY >> 2) + 0x8000);
  const noise = (fine - 0.5) * 0.16 + (coarse - 0.5) * 0.36;
  const value = sum + noise;
  return value <= 0 ? 0 : value;
}

/**
 * Writes a puddle's field into a buffer: `radius` by `radius` cells of `PUDDLE_CELL_PX` pixels, the puddle's centre in the
 * middle. The highest cell is kept as the puddle's `peakOf`, which then costs no second pass over the field.
 *
 * @param puddle The puddle.
 * @param out The cells, at least `radius * radius` long.
 * @returns The side in cells.
 */
export function fieldInto(puddle: Puddle, out: Float32Array): number {
  const side = puddle.radius;
  const x0 = puddle.x - puddle.radius;
  const y0 = puddle.y - puddle.radius;
  const gx = Math.round(x0 / PUDDLE_CELL_PX);
  const gy = Math.round(y0 / PUDDLE_CELL_PX);
  let peak = FULL_CUT;
  for (let cy = 0; cy < side; cy += 1) {
    for (let cx = 0; cx < side; cx += 1) {
      const at = cy * side + cx;
      out[at] = fieldAt(puddle, x0 + cx * PUDDLE_CELL_PX + 1, y0 + cy * PUDDLE_CELL_PX + 1, gx + cx, gy + cy);
      peak = Math.max(peak, out[at] as number);
    }
  }
  if (!peaks.has(puddle)) peaks.set(puddle, peak);
  return side;
}
